import {
  broadest,
  canManage,
  canManagePeople,
  isActiveMember,
  isReadOnly,
  ownsResource,
  parentProject,
  teamOf,
} from "./perms";
import {
  GrantDiffSchema,
  MemberIdSchema,
  levelOf,
  normalizeAccess,
  teamReaderKey,
  type AccessList,
  type AccessResource,
  type AccessUser,
  type ResourcePerm,
} from "./types";

export interface DerivedArrays {
  readers: string[];
  editors: string[];
}

// The arrays the rules and the team list queries read. Owner first, other uids
// sorted, the team key last; a Personal item names only its owner.
export function deriveArrays(resource: AccessResource): DerivedArrays {
  const { owner } = resource;
  const team = teamOf(resource);
  if (!team) return { readers: [owner], editors: [owner] };

  const readers = new Set<string>();
  const editors = new Set<string>();
  const grant = (uid: string, value: unknown) => {
    const level = levelOf(value);
    if (level === "none" || !MemberIdSchema.safeParse(uid).success) return;
    readers.add(uid);
    if (level === "edit") editors.add(uid);
  };
  const project = parentProject(resource);
  for (const list of [resource.access, project?.access]) {
    for (const [uid, level] of Object.entries(list?.members ?? {}))
      grant(uid, level);
  }
  if (project) grant(project.owner, "edit");
  const everyone = broadest(
    levelOf(resource.access?.everyone),
    levelOf(project?.access?.everyone),
  );
  readers.delete(owner);
  editors.delete(owner);

  const key = teamReaderKey(team);
  return {
    readers: [
      owner,
      ...[...readers].sort(),
      ...(everyone !== "none" ? [key] : []),
    ],
    editors: [
      owner,
      ...[...editors].sort(),
      ...(everyone === "edit" ? [key] : []),
    ],
  };
}

// The rules' canReadDoc/canWriteDoc over the stored arrays (without the staff
// and template-owner branches). Entries count only for active members of the
// item's team, so a Personal doc opens for its owner alone, whatever it stores.
// A lapsed team caps everyone at read, as resourcePerms does.
export function derivedPerms(
  user: AccessUser | null,
  resource: AccessResource,
  arrays: Partial<DerivedArrays>,
): ResourcePerm {
  const perm = arrayPerms(user, resource, arrays);
  return perm === "read_write" && isReadOnly(user, resource) ? "read" : perm;
}

function arrayPerms(
  user: AccessUser | null,
  resource: AccessResource,
  arrays: Partial<DerivedArrays>,
): ResourcePerm {
  if (ownsResource(user, resource)) return "read_write";
  const team = teamOf(resource);
  if (!user || !team || !isActiveMember(user, resource)) {
    return resource.publicLink ? "read" : "no_access";
  }
  const holds = (list: string[] | undefined) =>
    !!list && (list.includes(user.id) || list.includes(teamReaderKey(team)));
  if (holds(arrays.editors)) return "read_write";
  if (holds(arrays.readers) || resource.publicLink) return "read";
  return "no_access";
}

export type GrantDiffResult =
  | { ok: true; access: AccessList }
  | {
      ok: false;
      reason:
        | "invalid"
        | "personal"
        | "not_allowed"
        | "not_teammate"
        | "everyone_owner_only";
    };

// A Share-dialog save as operations. Editors may set or remove named
// teammates; only the owner changes Everyone. Entries the diff does not name
// keep their current value. `teammates` = the active members of the item's
// team: only they can be given an entry.
export function applyGrantDiff(
  user: AccessUser | null,
  resource: AccessResource,
  diff: unknown,
  teammates: readonly string[],
): GrantDiffResult {
  const parsed = GrantDiffSchema.safeParse(diff);
  if (!parsed.success) return { ok: false, reason: "invalid" };
  if (!teamOf(resource)) return { ok: false, reason: "personal" };
  if (!canManagePeople(user, resource))
    return { ok: false, reason: "not_allowed" };
  const outsider = Object.keys(parsed.data.set ?? {}).some(
    (uid) => uid !== resource.owner && !teammates.includes(uid),
  );
  if (outsider) return { ok: false, reason: "not_teammate" };

  const current = normalizeAccess(resource.access);
  const everyone = parsed.data.everyone ?? current.everyone ?? "none";
  if (everyone !== current.everyone && !canManage(user, resource)) {
    return { ok: false, reason: "everyone_owner_only" };
  }
  const members = { ...current.members };
  for (const uid of parsed.data.remove ?? []) delete members[uid];
  Object.assign(members, parsed.data.set);
  delete members[resource.owner];
  return { ok: true, access: { everyone, members } };
}
